import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import CASAdminPage from './pages/CASAdminPage';
import ServiceOperationsPage from './pages/ServiceOperationsPage';
import CASDashboardPage from './pages/CASDashboardPage';
import BusinessProfilePage from './pages/BusinessProfilePage';


// Create theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '1.5rem',
            '@media (min-width:600px)': {
                fontSize: '2.125rem',
            },
            '@media (min-width:900px)': {
                fontSize: '3rem',
            },
        },
        h2: {
            fontSize: '1.25rem',
            '@media (min-width:600px)': {
                fontSize: '1.5rem',
            },
            '@media (min-width:900px)': {
                fontSize: '2.125rem',
            },
        },
        h3: {
            fontSize: '1.125rem',
            '@media (min-width:600px)': {
                fontSize: '1.25rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontSize: '1rem',
            '@media (min-width:600px)': {
                fontSize: '1.25rem',
            },
            '@media (min-width:900px)': {
                fontSize: '2.125rem',
            },
        },
        h5: {
            fontSize: '0.875rem',
            '@media (min-width:600px)': {
                fontSize: '1rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.5rem',
            },
        },
        h6: {
            fontSize: '0.75rem',
            '@media (min-width:600px)': {
                fontSize: '0.875rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.25rem',
            },
        },
        body1: {
            fontSize: '0.875rem',
            '@media (min-width:600px)': {
                fontSize: '1rem',
            },
        },
        body2: {
            fontSize: '0.75rem',
            '@media (min-width:600px)': {
                fontSize: '0.875rem',
            },
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    minHeight: '44px', // Better touch target
                    '@media (max-width:600px)': {
                        minHeight: '48px',
                        fontSize: '0.875rem',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '@media (max-width:600px)': {
                        padding: '12px', // Larger touch target on mobile
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    '@media (max-width:600px)': {
                        margin: '16px',
                        maxHeight: 'calc(100% - 32px)',
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    overflowX: 'hidden', // Prevent horizontal scrolling globally
                },
                html: {
                    overflowX: 'hidden', // Prevent horizontal scrolling globally
                },
            },
        },
    },
});

function App() {
    return (
        <HelmetProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthProvider>
                    <Router>
                        <Routes>
                            {/* Public routes */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />

                            {/* Protected routes */}
                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <Layout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<Navigate to="/dashboard" replace />} />
                                <Route path="dashboard" element={<DashboardPage />} />
                                <Route path="profile" element={<ProfilePage />} />
                                <Route path="subscriptions" element={<SubscriptionsPage />} />
                                <Route path="service-operations" element={<ServiceOperationsPage />} />
                                <Route path="cas" element={<CASDashboardPage />} />
                                <Route path="cas-admin" element={<CASAdminPage />} />
                                <Route path="admin" element={<AdminPage />} />
                                <Route path="business-profile" element={<BusinessProfilePage />} />
                            </Route>
                        </Routes>
                    </Router>
                </AuthProvider>
            </ThemeProvider>
        </HelmetProvider>
    );
}

export default App; 